import { logger } from '../utils/logger';

const BLOCKING_TIME_METRIC_NAME = 'mainThreadBlockingTimeMs';
const DEFAULT_WINDOW_MS = 10_000;
const DEV_ATTRIBUTION_MIN_BLOCKING_MS = 50;

const noop = () => {};

// Local LoAF types: the DOM lib types for `long-animation-frame` aren't available on all our TS versions.
export interface LongAnimationFrameScript {
  name?: string;
  sourceURL?: string;
  sourceFunctionName?: string;
  invoker?: string;
  duration?: number;
  forcedStyleAndLayoutDuration?: number;
}

export interface LongAnimationFrameEntry {
  duration: number;
  blockingDuration: number;
  startTime: number;
  scripts?: LongAnimationFrameScript[];
}

export interface MainThreadBlockingMonitorOptions {
  windowMs?: number;
  isDev?: boolean;
  /** Injectable for testing. Returns a disconnect function. */
  observeLongAnimationFrames?: (onEntries: (entries: LongAnimationFrameEntry[]) => void) => () => void;
  reportDuration?: typeof logger.trackDuration;
  reportAttribution?: typeof logger.debug;
}

function supportsNativeLongAnimationFrames(): boolean {
  return (
    typeof PerformanceObserver !== 'undefined' &&
    !!PerformanceObserver.supportedEntryTypes?.includes('long-animation-frame')
  );
}

function observeNativeLongAnimationFrames(onEntries: (entries: LongAnimationFrameEntry[]) => void): () => void {
  const observer = new PerformanceObserver(list =>
    onEntries(list.getEntries() as unknown as LongAnimationFrameEntry[])
  );
  observer.observe({ type: 'long-animation-frame' } as PerformanceObserverInit);
  return () => observer.disconnect();
}

/**
 * Tracks main-thread responsiveness by aggregating Long Animation Frames (LoAF) into a single
 * summary metric per fixed window. Reporting one aggregate per window (rather than one event per
 * frame) keeps the reported volume constant regardless of how janky a session is: a constantly
 * blocked session emits the same number of events as a smooth one, just with larger values. This
 * avoids flooding Datadog and adding to the very lag we are measuring.
 */
export class MainThreadBlockingMonitor {
  private static activeMonitor: MainThreadBlockingMonitor | undefined;

  private frames: LongAnimationFrameEntry[] = [];
  private disconnectObserver: (() => void) | undefined;
  private intervalId: number | undefined;

  private readonly windowMs: number;
  private readonly isDev: boolean;
  private readonly observeLongAnimationFrames: (onEntries: (entries: LongAnimationFrameEntry[]) => void) => () => void;
  private readonly reportDuration: typeof logger.trackDuration;
  private readonly reportAttribution: typeof logger.debug;

  private readonly clearFrames = () => {
    this.frames = [];
  };

  static StartMainThreadBlockingMonitor(options: MainThreadBlockingMonitorOptions = {}): () => void {
    if (MainThreadBlockingMonitor.activeMonitor) {
      return MainThreadBlockingMonitor.activeMonitor.stopActiveMonitor;
    }

    if (!MainThreadBlockingMonitor.canStart(options)) {
      return noop;
    }

    const monitor = new MainThreadBlockingMonitor(options);
    monitor.start();
    MainThreadBlockingMonitor.activeMonitor = monitor;
    return monitor.stopActiveMonitor;
  }

  private static canStart(options: MainThreadBlockingMonitorOptions): boolean {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return false;
    }

    return !!options.observeLongAnimationFrames || supportsNativeLongAnimationFrames();
  }

  private constructor(options: MainThreadBlockingMonitorOptions) {
    this.windowMs = options.windowMs ?? DEFAULT_WINDOW_MS;
    this.isDev = options.isDev ?? import.meta.env.DEV;
    this.observeLongAnimationFrames = options.observeLongAnimationFrames ?? observeNativeLongAnimationFrames;
    this.reportDuration = options.reportDuration ?? logger.trackDuration;
    this.reportAttribution = options.reportAttribution ?? logger.debug;
  }

  private start(): void {
    this.disconnectObserver = this.observeLongAnimationFrames(entries => this.handleEntries(entries));
    document.addEventListener('visibilitychange', this.clearFrames);
    window.addEventListener('pagehide', this.clearFrames);
    this.intervalId = window.setInterval(() => this.flush(), this.windowMs);
  }

  private stop(): void {
    this.disconnectObserver?.();
    if (this.intervalId !== undefined) {
      window.clearInterval(this.intervalId);
    }
    document.removeEventListener('visibilitychange', this.clearFrames);
    window.removeEventListener('pagehide', this.clearFrames);
    this.disconnectObserver = undefined;
    this.intervalId = undefined;
    this.clearFrames();
  }

  private readonly stopActiveMonitor = () => {
    this.stop();

    if (MainThreadBlockingMonitor.activeMonitor === this) {
      MainThreadBlockingMonitor.activeMonitor = undefined;
    }
  };

  private handleEntries(entries: LongAnimationFrameEntry[]): void {
    if (!isPageVisible()) {
      return;
    }
    // A frame longer than the aggregation window usually means the renderer was suspended
    // (for example, sleep or a debugger pause), not that this window contained that much work.
    this.frames.push(...entries.filter(frame => frame.duration <= this.windowMs));
  }

  private flush(): void {
    const windowFrames = this.frames;
    this.frames = [];

    // Drop the window if the page turned hidden between collection and this flush.
    if (!isPageVisible() || windowFrames.length === 0) {
      return;
    }

    let blockingMs = 0;
    let maxFrameMs = 0;
    for (const frame of windowFrames) {
      blockingMs += frame.blockingDuration;
      if (frame.duration > maxFrameMs) {
        maxFrameMs = frame.duration;
      }
    }

    const windowEndTime = Date.now();
    const windowStartTime = windowEndTime - this.windowMs;

    this.reportDuration(BLOCKING_TIME_METRIC_NAME, {
      startTime: windowStartTime,
      duration: blockingMs,
      description: 'main-thread blocking time',
      context: { longFrameCount: windowFrames.length, maxFrameMs: Math.round(maxFrameMs), windowMs: this.windowMs },
    });

    // Attribution is only useful for local debugging; keep it out of production.
    if (this.isDev && blockingMs >= DEV_ATTRIBUTION_MIN_BLOCKING_MS) {
      this.reportAttribution(
        `main-thread long animation frame: ${Math.round(blockingMs)}ms blocking over the last ${this.windowMs / 1000}s (${windowFrames.length} frames, max ${Math.round(maxFrameMs)}ms)`
      );
    }
  }
}

function isPageVisible(): boolean {
  return document.visibilityState === 'visible';
}
